import { Box, Collapse, IconButton, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import InsightsIcon from '@mui/icons-material/Insights';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InboxIcon from '@mui/icons-material/Inbox';
import { tokens } from '../../global/theme/tokens';
import OverviewStatCard from '../../components/exo/project/task-view/overview/OverviewStatCard';

const MiniStat = ({ label, value, color }) => (
  <Box className="flex flex-col" sx={{ minWidth: 0 }}>
    <Typography
      variant="caption"
      sx={{
        opacity: 0.55,
        fontSize: '0.66rem',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ color, fontWeight: 700, fontSize: '0.95rem' }}>
      {value}
    </Typography>
  </Box>
);

// what the user has on their plate, accumulated over their projects
const MyWorkOverview = ({ stats, projectCount, userName }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(true);

  return (
    <Box
      className="shrink-0"
      sx={{
        background: `linear-gradient(135deg, ${colors.glassFrom}, ${colors.glassTo})`,
        border: `1px solid ${colors.selected}`,
        backdropFilter: 'blur(12px)',
        borderRadius: '16px',
        padding: { xs: 1, md: 1.25 },
        display: 'flex',
        flexDirection: 'column',
        gap: isOpen ? 1.75 : 0,
        transition: 'gap 0.2s ease',
      }}
    >
      <Box
        onClick={() => setIsOpen(!isOpen)}
        className="flex flex-wrap items-center gap-x-2 gap-y-1 cursor-pointer"
        sx={{
          paddingX: 0.5,
          paddingY: 0.25,
          borderRadius: '8px',
          ':hover': { backgroundColor: `${colors.selected}40` },
        }}
      >
        <InsightsIcon sx={{ fontSize: '18px', color: colors.info }} />

        <Box
          className="flex flex-col sm:flex-row sm:items-baseline gap-x-2"
          sx={{ flex: '1 1 0%', minWidth: 0 }}
        >
          <Typography fontSize="16px" fontWeight={600} noWrap>
            {t('My overview')}
          </Typography>
          <Typography fontSize="11px" noWrap className="opacity-60">
            {[
              userName,
              `${projectCount} ${t('Projects')}`,
              `${stats.openScore} ${t('Points')} ${t('open')}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        </Box>

        {!isOpen && (
          <Box className="flex items-center justify-between sm:justify-end gap-4 sm:gap-6 w-full sm:w-auto shrink-0 pr-1 order-last sm:order-none">
            <MiniStat label={t('Open')} value={stats.open} />
            <MiniStat
              label={t('Overdue')}
              value={stats.overdue}
              color={stats.overdue ? colors.error : null}
            />
            <MiniStat
              label={t('In pool')}
              value={stats.pool}
              color={stats.pool ? colors.warning : null}
            />
          </Box>
        )}

        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
        >
          <KeyboardArrowRightIcon
            sx={{
              transform: `rotate(${isOpen ? 90 : 0}deg)`,
              transition: 'transform 200ms',
            }}
          />
        </IconButton>
      </Box>

      <Collapse in={isOpen} unmountOnExit>
        <Box
          sx={{
            display: 'grid',
            gap: 1.25,
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          }}
        >
          <OverviewStatCard
            icon={<RadioButtonUncheckedIcon sx={{ fontSize: '15px' }} />}
            label={t('Open tasks')}
            value={stats.open}
            caption={`${t('in')} ${stats.projectsWithOpen} ${t('of')} ${projectCount} ${t('Projects')}`}
            color={colors.info}
          />
          <OverviewStatCard
            icon={<ErrorOutlineIcon sx={{ fontSize: '15px' }} />}
            label={t('Deadline exceeded')}
            value={stats.overdue}
            caption={t('overdue')}
            color={colors.error}
          />
          <OverviewStatCard
            icon={<CheckCircleOutlineIcon sx={{ fontSize: '15px' }} />}
            label={t('Open points')}
            value={stats.openScore}
            caption={t('planned')}
            color={colors.success}
          />
          <OverviewStatCard
            icon={<InboxIcon sx={{ fontSize: '15px' }} />}
            label={t('In pool')}
            value={stats.pool}
            caption={t('available')}
            color={colors.warning}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export default MyWorkOverview;
